import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import LanguageToggle from '../components/LanguageToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreateBookletFromBankPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    subject: '',
    difficulty: '',
    exam_context: ''
  });
  const [bookletTitle, setBookletTitle] = useState('');
  const [shareWithTeachers, setShareWithTeachers] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    if (user?.role === 'teacher') {
      fetchQuestions();
    }
  }, [filters]);

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/questions/bank`, {
        params: filters,
        withCredentials: true
      });
      setQuestions(response.data.questions || []);
    } catch (error) {
      console.error('Error fetching questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleQuestionSelection = (questionId) => {
    if (selectedQuestions.includes(questionId)) {
      setSelectedQuestions(selectedQuestions.filter(id => id !== questionId));
    } else {
      setSelectedQuestions([...selectedQuestions, questionId]);
    }
  };

  const handleCreateBooklet = async () => {
    if (!bookletTitle.trim()) {
      alert(language === 'tr' ? 'Lütfen kitapçık başlığı girin' : 'Please enter booklet title');
      return;
    }

    if (selectedQuestions.length === 0) {
      alert(language === 'tr' ? 'Lütfen en az bir soru seçin' : 'Please select at least one question');
      return;
    }

    try {
      await axios.post(
        `${API}/personalization/create-booklet-from-bank`,
        {
          title: bookletTitle,
          question_ids: selectedQuestions,
          share_with_teachers: shareWithTeachers
        },
        { withCredentials: true }
      );

      const message = shareWithTeachers
        ? (language === 'tr' 
          ? `✅ "${bookletTitle}" kitapçığı oluşturuldu ve öğretmenlerle paylaşıldı!` 
          : `✅ Booklet "${bookletTitle}" created and shared with teachers!`)
        : (language === 'tr' 
          ? `✅ "${bookletTitle}" kitapçığı başarıyla oluşturuldu!` 
          : `✅ Booklet "${bookletTitle}" created successfully!`);
      
      alert(message);
      
      setShowCreateModal(false);
      setSelectedQuestions([]);
      setBookletTitle('');
      setShareWithTeachers(false);
      navigate('/teacher/my-booklets');
    } catch (error) {
      console.error('Error creating booklet:', error);
      alert(language === 'tr' 
        ? '❌ Kitapçık oluşturulurken hata oluştu' 
        : '❌ Error creating booklet');
    }
  };

  if (user?.role !== 'teacher') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">
            {language === 'tr' ? 'Bu özellik sadece öğretmenler için' : 'This feature is for teachers only'}
          </h1>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            {language === 'tr' ? 'Ana Sayfaya Dön' : 'Back to Home'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <LanguageToggle />
      <Navbar title={language === 'tr' ? 'Soru Bankasından Kitapçık Oluştur' : 'Create Booklet from Bank'} />

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {language === 'tr' ? '📚 Özel Kitapçık Oluştur' : '📚 Create Custom Booklet'}
              </h1>
              <p className="text-gray-600">
                {language === 'tr'
                  ? 'Soru bankasından istediğiniz soruları seçip öğrencileriniz için özel kitapçık oluşturun'
                  : 'Select questions from the bank and create custom booklets for your students'}
              </p>
            </div>
            
            {selectedQuestions.length > 0 && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-green-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-green-700 transition-all shadow-lg"
              >
                ✅ {language === 'tr' ? `Kitapçık Oluştur (${selectedQuestions.length} soru)` : `Create Booklet (${selectedQuestions.length} questions)`}
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">
            {language === 'tr' ? 'Filtreler' : 'Filters'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {language === 'tr' ? 'Ders' : 'Subject'}
              </label>
              <select
                value={filters.subject}
                onChange={(e) => setFilters({ ...filters, subject: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{language === 'tr' ? 'Tümü' : 'All'}</option>
                <option value="Physics">{language === 'tr' ? 'Fizik' : 'Physics'}</option>
                <option value="Chemistry">{language === 'tr' ? 'Kimya' : 'Chemistry'}</option>
                <option value="Biology">{language === 'tr' ? 'Biyoloji' : 'Biology'}</option>
                <option value="Mathematics">{language === 'tr' ? 'Matematik' : 'Mathematics'}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {language === 'tr' ? 'Zorluk' : 'Difficulty'}
              </label>
              <select
                value={filters.difficulty}
                onChange={(e) => setFilters({ ...filters, difficulty: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{language === 'tr' ? 'Tümü' : 'All'}</option>
                <option value="easy">{language === 'tr' ? 'Kolay' : 'Easy'}</option>
                <option value="medium">{language === 'tr' ? 'Orta' : 'Medium'}</option>
                <option value="hard">{language === 'tr' ? 'Zor' : 'Hard'}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {language === 'tr' ? 'Sınav Tipi' : 'Exam Type'}
              </label>
              <select
                value={filters.exam_context}
                onChange={(e) => setFilters({ ...filters, exam_context: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{language === 'tr' ? 'Tümü' : 'All'}</option>
                <option value="TYT">TYT</option>
                <option value="AP">AP</option>
              </select>
            </div>
          </div>
        </div>

        {/* Questions List */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">
            {language === 'tr' ? `Sorular (${questions.length})` : `Questions (${questions.length})`}
          </h2>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">{language === 'tr' ? 'Yükleniyor...' : 'Loading...'}</p>
            </div>
          ) : questions.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">{language === 'tr' ? 'Soru bulunamadı' : 'No questions found'}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {questions.map((question) => (
                <div
                  key={question.id}
                  className={`border-2 rounded-xl p-4 cursor-pointer transition-all ${
                    selectedQuestions.includes(question.id)
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                  onClick={() => toggleQuestionSelection(question.id)}
                >
                  <div className="flex items-start">
                    <div className="mr-4">
                      <input
                        type="checkbox"
                        checked={selectedQuestions.includes(question.id)}
                        onChange={() => toggleQuestionSelection(question.id)}
                        className="w-5 h-5 text-green-600 rounded focus:ring-green-500"
                      />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-semibold">
                          {question.subject}
                        </span>
                        <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-xs font-semibold">
                          {question.difficulty}
                        </span>
                        {question.exam_context && question.exam_context.map((ctx, idx) => (
                          <span key={idx} className="bg-orange-100 text-orange-800 px-2 py-1 rounded text-xs font-semibold">
                            {ctx}
                          </span>
                        ))}
                      </div>
                      <p className="text-gray-800 font-medium line-clamp-2">
                        {language === 'tr' ? question.stem_TR : question.stem_EN}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create Booklet Modal */}
      {showCreateModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={() => setShowCreateModal(false)}
        >
          <div
            className="bg-white rounded-2xl max-w-lg w-full p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {language === 'tr' ? 'Kitapçık Oluştur' : 'Create Booklet'}
            </h2>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {language === 'tr' ? 'Kitapçık Başlığı' : 'Booklet Title'}
              </label>
              <input
                type="text"
                value={bookletTitle}
                onChange={(e) => setBookletTitle(e.target.value)}
                placeholder={language === 'tr' ? 'Örn: Fizik - Newton Kanunları' : 'e.g. Physics - Newton Laws'}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="mb-6 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-gray-700 mb-3">
                <strong>{selectedQuestions.length}</strong> {language === 'tr' ? 'soru seçildi' : 'questions selected'}
              </p>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="shareWithTeachers"
                  checked={shareWithTeachers}
                  onChange={(e) => setShareWithTeachers(e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <label htmlFor="shareWithTeachers" className="ml-2 text-sm text-gray-700">
                  {language === 'tr' 
                    ? '🌐 Diğer öğretmenlerle paylaş (limitsiz)' 
                    : '🌐 Share with other teachers (unlimited)'}
                </label>
              </div>
              
              {!shareWithTeachers && (
                <p className="text-xs text-gray-500 mt-2">
                  {language === 'tr'
                    ? '⚠️ Paylaşılmayan kitapçıklar için limit: 10 adet'
                    : '⚠️ Private booklets limit: 10'}
                </p>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 px-6 py-3 rounded-lg font-semibold transition"
              >
                {language === 'tr' ? 'İptal' : 'Cancel'}
              </button>
              <button
                onClick={handleCreateBooklet}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition"
              >
                {language === 'tr' ? '✅ Oluştur' : '✅ Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateBookletFromBankPage;
